import {useUserPermittedEditingDoc} from '../../../../../hooks/user/use-has-permission';
import {useDocumentQueryCache} from '../../hooks/use-document-query';
import {useOntologyDocsQueryCache} from '../../hooks/use-ontology-docs-query';
import {useUnpublishedDocsQueryCache} from '../../hooks/use-unpublished-docs-query';
import {useSearchDocsCache} from '../../sidebar/search-layout/hooks/use-edit-mode-search-query-cache';
import {useCreateWorkingCopy} from '../../hooks/use-create-working-copy-mutation';
import {useFetchWorkingCopyQueryCache} from '../../hooks/use-fetch-working-copy-query';

interface Props {
    document: EditModeDoc;
    revisionId?: string;
}

export function useStartEditingDocument(props: Props) {
    const searchDocsQueryCache = useSearchDocsCache();
    const ontologyDocsQueryCache = useOntologyDocsQueryCache();
    const documentQueryCache = useDocumentQueryCache();
    const unpublishedDocsQueryCache = useUnpublishedDocsQueryCache();
    const wcQueryCache = useFetchWorkingCopyQueryCache();
    const isUserPermittedToEdit = useUserPermittedEditingDoc(props.document);

    const workingCopyMutation = useCreateWorkingCopy({
        accession: props.document.accession,
        revisionId: props.revisionId
    });

    const handleEdit = async () => {
        const wc = await workingCopyMutation.mutateAsync();

        const nextDocument: EditModeDoc = {
            ...props.document,
            readOnly: false,
            status: 'EDITABLE',
            editedByCurrentUser: true
        };

        searchDocsQueryCache.updateDocument(nextDocument);
        ontologyDocsQueryCache.updateDocument(nextDocument);

        documentQueryCache.set(nextDocument);
        unpublishedDocsQueryCache.addDocument(nextDocument);
        wcQueryCache.addDocument(nextDocument, wc);
    };

    const editingDisabledReason = (function () {
        if (props.document.readOnly) {
            return 'This document is read-only.';
        }

        if (!isUserPermittedToEdit) {
            return 'You are not authorised to edit this document.';
        }

        return undefined;
    })();

    return {
        startEditing: handleEdit,
        isDocEditable: !editingDisabledReason,
        editingDisabledReason
    };
}
